import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { Mat4 } from '../../engine/math/mat4';
import { Quat } from '../../engine/math/quat';
import { box } from '../../engine/geo/primitives';
import { GpuMesh, INSTANCE_STRIDE } from '../../engine/render/GpuMesh';
import type { Color } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';

interface P { x: number; y: number; z: number; vx: number; vy: number; vz: number; life: number; maxLife: number; size: number; r: number; g: number; b: number; e: number; gravity: number; bounce: boolean; rot: number; rotV: number; shrink: boolean }

/** Cube particle pool rendered as one instanced draw. Bursts, puffs, drifting motes. */
export class Particles {
  private readonly pool: P[] = [];
  private readonly buf: Float32Array;
  private readonly gm: GpuMesh;
  private readonly m = new Mat4();
  private readonly q = new Quat();
  private readonly s = new Vec3();
  private readonly p = new Vec3();
  private count = 0;

  constructor(private readonly ctx: Ctx, readonly max = 600) {
    this.buf = new Float32Array(max * INSTANCE_STRIDE);
    this.gm = ctx.renderer.createMesh(box(1, 1, 1, [1, 1, 1]), 'particles');
    this.gm.setInstances(new Float32Array(0), true);
    ctx.spawn('particles', new Transform(), this.gm, { instanced: true, castShadow: false, noCull: true });
  }

  private emit(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size: number, c: Color, e: number, gravity: number, bounce: boolean, shrink = true): void {
    if (this.pool.length >= this.max) this.pool.shift();
    this.pool.push({ x, y, z, vx, vy, vz, life, maxLife: life, size, r: c[0], g: c[1], b: c[2], e, gravity, bounce, rot: Math.random() * 6, rotV: (Math.random() - 0.5) * 8, shrink });
  }

  /** Burst of cubes flying outward (hits, kills). */
  burst(pos: Vec3, n: number, color: Color, speed = 5, size = 0.18, life = 0.8, emissive = 0.4): void {
    for (let i = 0; i < n; i++) {
      const th = Math.random() * Math.PI * 2, ph = Math.random() * Math.PI - Math.PI / 2;
      const sp = speed * (0.5 + Math.random() * 0.7);
      this.emit(pos.x, pos.y, pos.z, Math.cos(th) * Math.cos(ph) * sp, Math.abs(Math.sin(ph)) * sp + speed * 0.4, Math.sin(th) * Math.cos(ph) * sp, life * (0.7 + Math.random() * 0.6), size * (0.6 + Math.random() * 0.8), color, emissive, 14, true);
    }
  }

  /** Low dust puff (drops, landings). */
  puff(pos: Vec3, n = 8, color: Color = [0.75, 0.68, 0.55], size = 0.14): void {
    for (let i = 0; i < n; i++) {
      const th = Math.random() * Math.PI * 2, sp = 1 + Math.random() * 1.5;
      this.emit(pos.x, pos.y + 0.05, pos.z, Math.cos(th) * sp, 0.6 + Math.random() * 1.2, Math.sin(th) * sp, 0.5 + Math.random() * 0.3, size, color, 0, 3, false);
    }
  }

  /** Slow drifting mote (dandelion seeds, fireflies, sand). */
  mote(pos: Vec3, vel: Vec3, life: number, size: number, color: Color, emissive = 0, gravity = 0): void {
    this.emit(pos.x, pos.y, pos.z, vel.x, vel.y, vel.z, life, size, color, emissive, gravity, false, false);
  }

  /** Ring of splash droplets. */
  splash(pos: Vec3, n = 10, color: Color = [0.6, 0.8, 0.85]): void {
    for (let i = 0; i < n; i++) {
      const th = (i / n) * Math.PI * 2 + Math.random() * 0.3, sp = 1.5 + Math.random();
      this.emit(pos.x, pos.y, pos.z, Math.cos(th) * sp, 2.5 + Math.random() * 1.5, Math.sin(th) * sp, 0.6, 0.1, color, 0.2, 12, false);
    }
  }

  update(dt: number): void {
    const st = this.ctx.statics;
    let w = 0;
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      p.life -= dt;
      if (p.life <= 0) continue;
      p.vy -= p.gravity * dt;
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
      p.rot += p.rotV * dt;
      if (p.bounce) {
        const gy = st.terrainHeight(p.x, p.z) + p.size * 0.5;
        if (p.y < gy) { p.y = gy; p.vy = -p.vy * 0.35; p.vx *= 0.7; p.vz *= 0.7; p.rotV *= 0.5; }
      }
      this.pool[w++] = p;
    }
    this.pool.length = w;
    // write instance records
    this.count = Math.min(w, this.max);
    for (let i = 0; i < this.count; i++) {
      const p = this.pool[i];
      const k = p.life / p.maxLife;
      const sz = p.shrink ? p.size * (0.3 + 0.7 * k) : p.size * Math.min(1, k * 4);
      this.s.set(sz, sz, sz);
      this.q.setEuler(p.rot, p.rot * 0.7, 0);
      this.p.set(p.x, p.y, p.z);
      this.m.compose(this.p, this.q, this.s);
      GpuMesh.writeInstance(this.buf, i, this.m.e, p.r, p.g, p.b, p.e);
    }
    this.gm.setInstances(this.buf.subarray(0, this.count * INSTANCE_STRIDE), true);
  }
  get active(): number { return this.pool.length; }
}
